import { useEffect, useRef, useState } from "react";
import ShapeSelector from "./ShapeSelector";
import CanvasEditor from "./CanvasEditor";
import { uploadPlotShape } from "../../utils/azure-storage-blob";
import { _axios } from "../../utils/axios";
import { ErrorToast, SuccessToast } from "../../components/toastMessage";

interface Shape {
  id: number;
  name: string;
  image_path: string;
  svg_template?: string;
  category?: string;
}

interface Props {
  application: any;
  onBack: () => void;
  onForward: () => void;
}

interface CalculationState {
  plotArea: number;
  roadArea: number;
  kPartArea: number;
  netArea: number;
  canvasData: any[];
  inputValues: Record<string, number>;
  calculationPosition: {
    x: number;
    y: number;
  };
}

function dataURLtoFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

const SchematicPlanPage = ({ application, onBack, onForward }: Props) => {

  const [shape, setShape] = useState<Shape | null>(null);
  const [schematicImage, setSchematicImage] = useState<string | null>(null);
  const [calculation, setCalculation] = useState<CalculationState>({
    plotArea: 0,
    roadArea: 0,
    kPartArea: 0,
    netArea: 0,
    canvasData: [],
    inputValues: {},
    calculationPosition: { x: 20, y: 20 }
  });
  const [lastSavedImage, setLastSavedImage] = useState<string | null>(null);
  const [isFinalSaved, setIsFinalSaved] = useState(false);
  const autoSaveRef = useRef<any>(null);
  const [direction, setDirection] = useState<Shape | null>(null);
  const [isNetAreaManual, setIsNetAreaManual] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);

  const handleSave = async () => {
    if (!schematicImage) {
      ErrorToast("Generate schematic first");
      return;
    }

    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current);
    }

    const file = dataURLtoFile(
      schematicImage,
      `schematic_${Date.now()}.png`
    );

    const url = await uploadPlotShape(
      file,
      application.id + "_" + Date.now(),
      "Schematic_Dimension"
    );

    sessionStorage.setItem(
      `schematic_${application.applicationId}`,
      url
    );

    // CALL BACKEND
    await _axios.post("/schematic/save-final", {
      certificate_id: application.id,
      applicationId: application.applicationId,
      image_url: url
    });

    setIsFinalSaved(true);

    SuccessToast("Final Schematic Saved");

    onForward();
  };

  const handleSaveDraft = async () => {
    if (isFinalSaved) {
      console.log("Draft blocked after final save");
      return;
    }

    let url = lastSavedImage;

    if (schematicImage && schematicImage !== lastSavedImage) {
      const file = dataURLtoFile(schematicImage, `schematic_${Date.now()}.png`);

      url = await uploadPlotShape(
        file,
        application.id + "_" + Date.now(),
        "Schematic_Draft"
      );

      setLastSavedImage(url);
    }

    console.log("Sending certificate_id:", application);

    await _axios.post("/schematic/save-draft", {
      certificate_id: application.id,
      image_url: url || null,
      canvas_data: {
        ...calculation,
        isNetAreaManual,
        shapeId: shape?.id,
        templateImage: shape?.image_path,
        shapeName: shape?.name,
        shapeType: shape?.category,
        directionName: direction?.name,
        directionImage: direction?.image_path
      }
    });
    console.log("Shape object:", shape);

    SuccessToast("Draft Saved");
  };

  useEffect(() => {
    const loadDraft = async () => {
      const res = await _axios.get(
        `/schematic/get-draft/${application.id}`
      );

      if (!res.data) return;

      const data = res.data;
      console.log("Draft canvas_data:", data);

      setSchematicImage(data.image_url);

      setCalculation({
        plotArea: data.canvas_data?.plotArea || 0,
        roadArea: data.canvas_data?.roadArea || 0,
        kPartArea: data.canvas_data?.kPartArea || 0,
        netArea: data.canvas_data?.netArea || 0,
        canvasData: data.canvas_data?.canvasData || [],
        inputValues: data.canvas_data?.inputValues || {},
        calculationPosition: data.canvas_data?.calculationPosition || { x: 20, y: 20 }
      });

      setIsNetAreaManual(
        data.canvas_data?.isNetAreaManual ?? false
      );

      if (data.canvas_data?.shapeId) {
        const loadedShape = {
          id: data.canvas_data.shapeId,
          name: data.canvas_data.shapeName,
          image_path: data.canvas_data.templateImage,
          category: data.canvas_data.shapeType ?? "RECTANGLE SHAPE"
        };
        console.log("✅ Draft shape loaded:", loadedShape);
        setShape(loadedShape);
      } else {
        console.log("⚠️ Draft exists but no shapeId found");
      }

      if (data.canvas_data.directionName) {
        const res = await _axios.get("/schematic/shapes", {
          params: { type: "DIRECTION" }
        });

        const matchedDirection = res.data.find(
          (d: any) => d.name === data.canvas_data.directionName
        );

        if (matchedDirection) {
          setDirection({
            ...matchedDirection,
            category: "DIRECTION"
          });
        }
      }

      // Signal draft loading complete
      console.log("✅ Draft loading complete");
      setIsDraftLoaded(true);
    };

    loadDraft();
  }, []);

// Set default "Rectangle Shape" if no draft shape exists
  useEffect(() => {
    console.log("🎯 Default check - isDraftLoaded:", isDraftLoaded, "shape:", shape);
    if (isDraftLoaded && !shape) {
      console.log("✅ Setting default: Rectangle Shape");
      setSelectedCategory("Rectangle Shape");
    }
  }, [isDraftLoaded, shape]);

  // Track selectedCategory changes
  useEffect(() => {
    console.log("📌 selectedCategory updated to:", selectedCategory);
  }, [selectedCategory]);

  // Sync selectedCategory with shape.category whenever shape changes
  useEffect(() => {
    if (shape?.category) {
      console.log("🔗 Syncing selectedCategory with shape.category:", shape.category);
      setSelectedCategory(shape.category);
    }
  }, [shape]);

  const lastSavedRef = useRef<string>("");

  useEffect(() => {
    if (!schematicImage || isFinalSaved) return;

    const current = JSON.stringify(calculation);

    if (current === lastSavedRef.current) return;

    lastSavedRef.current = current;

    autoSaveRef.current = setTimeout(() => {
      handleSaveDraft();
    }, 8000);

    return () => clearTimeout(autoSaveRef.current);
  }, [calculation, isFinalSaved]);

  return (

    <div className="p-6">

      <h2 className="text-[#423F3F] text-xl font-bold mb-4">
        Schematic Plan
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

        {/* LEFT PANEL */}
        <div className="lg:col-span-1 space-y-4">
          {/* SHAPE TYPE SELECTOR */}
          <ShapeSelector
              onSelect={(selectedShape) => {
                console.log("🎨 Shape selected:", selectedShape);
                setShape(selectedShape);
              }}
              selectedShapeId={shape?.id}
              selectedType={shape?.category || selectedCategory}
          />

          {/* DIRECTION SELECTOR */}
          <ShapeSelector
            onSelect={setDirection}
            category="DIRECTION"
            selectedShapeId={direction?.id}
          />
          {shape && (
            <div className="border rounded-md p-3 bg-gray-50">
              <h3 className="text-[#423F3F] font-semibold text-sm mb-2">
                Shape Preview
              </h3>
              <img
                src={shape.image_path || shape.svg_template}
                className="w-full max-h-[250px] object-contain"
              />
            </div>
          )}
          {direction && (
            <button
              className="bg-red-500 text-white px-3 py-1 rounded-md w-full"
              onClick={() => setDirection(null)}
            >
              Remove Direction
            </button>
          )}
        </div>

        {/* CANVAS */}
        <div className="lg:col-span-3 space-y-4">

          {shape && (
            <CanvasEditor
              shape={shape}
              direction={direction}
              calculation={calculation}
              initialData={calculation.canvasData}
              onExport={(data) => {
                console.log("Parent received schematic:", data);

                setSchematicImage(data.image);
                sessionStorage.setItem(
                  `schematic_${application.applicationId}`,
                  data.image
                );

                setCalculation(prev => ({
                  ...prev,
                  netArea: isNetAreaManual ? prev.netArea : data.netArea,
                  canvasData: data.canvasData,
                  inputValues: data.inputValues || prev.inputValues,
                  calculationPosition: data.calculationPosition,
                  shapeName: data.shapeName,
                  shapeType: data.shapeType,
                  directionName: data.directionName
                }));
              }}
              isFinalSaved={isFinalSaved}
            />
          )}

          {/* Calculation Panel */}
          {shape && (
            <div className="bg-white border rounded-md rounded-lg p-4 shadow-sm">
              <h3 className="text-[#423F3F] font-semibold mb-3">
                Reference
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">

                <div>PLOT AREA AS PER SALE DEED / 7/12</div>
                <input
                  type="number"
                  style={{ MozAppearance: "textfield" }}
                  className="border px-2 py-1 rounded w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={calculation.plotArea}
                  onChange={(e) =>
                    setCalculation({
                      ...calculation,
                      plotArea: Number(e.target.value)
                    })
                  }
                />

                <div>DEDUCT AREA FOR ___M TANGENT</div>
                <input
                  type="number"
                  style={{ MozAppearance: "textfield" }}
                  className="border px-2 py-1 rounded w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={calculation.kPartArea}
                  onChange={(e) =>
                    setCalculation({
                      ...calculation,
                      kPartArea: Number(e.target.value)
                    })
                  }
                />

                <div>DEDUCT AREA UNDER ROAD WIDENING</div>
                <input
                  type="number"
                  style={{ MozAppearance: "textfield" }}
                  className="border px-2 py-1 rounded w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={calculation.roadArea}
                  onChange={(e) =>
                    setCalculation({
                      ...calculation,
                      roadArea: Number(e.target.value)
                    })
                  }
                />

                <div>NET AREA REGULARIZED</div>
                <input
                  type="number"
                  style={{ MozAppearance: "textfield" }}
                  className="border px-2 py-1 rounded w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={calculation.netArea}
                  onChange={(e) => {
                    setIsNetAreaManual(true);
                    setCalculation(prev => ({
                      ...prev,
                      netArea: Number(e.target.value)
                    }));
                  }}
                />

              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">

        <button
          className="bg-black text-white px-10 py-2 grow md:grow-0 rounded-full font-bold"
          onClick={onBack}
        >
          Back
        </button>

        <button
          className="bg-primary px-10 py-2 grow md:grow-0 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          disabled={!schematicImage}
          onClick={handleSaveDraft}
        >
          Save
        </button>

        <button
          className="bg-primary px-10 py-2 grow md:grow-0 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          onClick={handleSave}
        >
          Next
        </button>

      </div>
    </div>

  );
};

export default SchematicPlanPage;